import React, { useCallback } from "react";
import { Label } from "../../../../../components/ui/label";
import { Button } from "../../../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../../../../../components/ui/radio-group";
import { useCentralStore } from "../../../../../store/Central";
import { showNotification } from "../../../../../store/Central/selectors";
import { Plus } from "lucide-react";
import { Region } from "../../../../../types/enums";
import { REGION_OPTIONS, RegionMetadata, RegionConfig } from "../../../../../types/region.types";
import { RegionCard } from "./RegionCard";

interface CountriesSectionProps {
	handleUpdateField: (index: number, field: keyof RegionConfig, value: any) => void;
}

export const CountriesSection: React.FC<CountriesSectionProps> = ({ handleUpdateField }) => {
	const { organization, activatedRegions, updateDefaultRegion, addRegionAndSave, removeRegionAndSave } = useCentralStore(
		state => ({
			organization: state.organization,
			activatedRegions: state.activatedRegions,
			updateDefaultRegion: state.updateDefaultRegion,
			addRegionAndSave: state.addRegionAndSave,
			removeRegionAndSave: state.removeRegionAndSave,
		})
	);
	const [selectedNewRegion, setSelectedNewRegion] = React.useState<string>("");

	const handleAddRegion = useCallback(async () => {
		if (!selectedNewRegion) {
			showNotification({
				message: "Bitte wählen Sie eine Region aus",
				type: "error",
			});
			return;
		}

		const regionCode = parseInt(selectedNewRegion) as Region;
		await addRegionAndSave(regionCode);
		setSelectedNewRegion("");
	}, [selectedNewRegion, addRegionAndSave]);

	const handleRemoveRegion = useCallback(async (index: number) => {
		await removeRegionAndSave(index);
	}, [removeRegionAndSave]);

	const getRegionInfo = useCallback((regionCode: Region): RegionMetadata | undefined => {
		return REGION_OPTIONS.find(r => r.value === regionCode);
	}, []);

	// Get available regions (not yet activated)
	const availableRegions = REGION_OPTIONS.filter(
		option => !activatedRegions.some(r => r.region === option.value)
	);

	return (
		<div className="mb-8">
			<div className="mb-3">
				<h2 className="text-lg font-medium text-gray-900">Länder</h2>
				<p className="text-sm text-gray-600 mt-1">
					Länder bestimmen, welche Tarifwerke standardmässig angezeigt werden sowie die Steuereinstellungen.
				</p>
			</div>
			<div className="bg-white rounded-lg shadow-sm border border-gray-200">
				{/* Add new region section */}
				{availableRegions.length > 0 && (
					<div className="p-4 border-b border-gray-200">
						<div className="flex items-center gap-3">
							<Select value={selectedNewRegion} onValueChange={setSelectedNewRegion}>
								<SelectTrigger className="w-64">
									<SelectValue placeholder="Region hinzufügen..." />
								</SelectTrigger>
								<SelectContent>
									{availableRegions.map(region => (
										<SelectItem key={region.value} value={region.value.toString()}>
											<span>{region.label}</span>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<Button
								onClick={handleAddRegion}
								size="sm"
								variant="outline"
							>
								<Plus className="h-4 w-4" />
							</Button>
						</div>
					</div>
				)}

				{/* Default region selection */}
				{activatedRegions.length > 1 && (
					<div className="p-4 border-b border-gray-200 bg-gray-50">
						<Label className="text-sm font-medium mb-2 block">Standard-Region</Label>
						<RadioGroup
							value={organization?.default_activated_region_id?.toString() || ""}
							onValueChange={(value) => updateDefaultRegion(value ? parseInt(value) : null)}
							className="flex gap-4"
						>
							{activatedRegions.map((region) => {
								const regionInfo = getRegionInfo(region.region);
								return region.id ? (
									<div key={region.id} className="flex items-center space-x-2">
										<RadioGroupItem value={region.id.toString()} id={`default-${region.id}`} />
										<Label htmlFor={`default-${region.id}`} className="flex items-center gap-1 cursor-pointer">
											<span>{regionInfo?.label}</span>
										</Label>
									</div>
								) : null;
							})}
						</RadioGroup>
					</div>
				)}

				{/* Activated regions list */}
				{activatedRegions.length === 0 ? (
					<div className="p-8 text-center text-gray-500">
						Keine Regionen aktiviert
					</div>
				) : (
					<div className="divide-y divide-gray-200">
						{activatedRegions.map((region, index) => {
							const regionInfo = getRegionInfo(region.region);
							const isDefault = region.id === organization?.default_activated_region_id;
							return (
								<RegionCard
									key={region.region}
									region={region}
									index={index}
									regionInfo={regionInfo}
									isDefault={isDefault}
									onUpdateField={handleUpdateField}
									onRemove={handleRemoveRegion}
								/>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
};